import React, { Key } from "react";

import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useGetVero1Checklist } from "manager/hooks/api/vero1Queries";

import { InternalOnlyChip } from "./InternalOnlyChip";
import { openDealReview } from "./openDealReview";

import { rereviewMessage } from "./Vero1RereviewBand";
import { Vero1ReviewTrigger } from "./Vero1ReviewPanel";
import { formatRunStamp } from "./vero1Dates";

const Band = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  /* one grammar with the banner's other internal rows (26px inset) */
  padding: 11px 26px;
  background: #faf9fd;
  border-top: 1px solid #edecf2;
`;

const Info = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
`;

const Primary = styled.span`
  font: 400 12.5px "Work Sans", sans-serif;
  color: #2d2943;
  white-space: nowrap;
  flex: 0 0 auto;
`;

/* never truncated: when tight the hint wraps inside the text block while
   the trigger stays put */
const Hint = styled.span`
  font: 300 12px "Work Sans", sans-serif;
  color: #8b87a0;
  flex: 0 1 auto;
  min-width: 0;
`;

/* a wrapped hint aligns with the message, never under the chip */
const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
`;

const TextRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  min-width: 0;
`;

const Action = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
`;

/* Spec 4.4: "Full findings are one click away in Deal review." */
const OpenReviewLink = styled.button`
  font: 500 12.5px "Work Sans", sans-serif;
  color: #8052d0;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;
`;

export interface Vero1HoldBandProps {
  dealId: Key;
  vero1Active?: boolean;
  dealLabel?: string;
  runAt?: string | null;
}

const runLabel = (runAt?: string | null): string => {
  if (!runAt) return "Lumen screening";
  return `Lumen screening · ${formatRunStamp(runAt)}`;
};

export const Vero1HoldBand = ({
  dealId,
  vero1Active,
  dealLabel,
  runAt,
}: Vero1HoldBandProps) => {
  // one internal surface: when a re-screen set the review aside, this band
  // absorbs the notice band's message instead of stacking two chips
  const { rereview } = useGetVero1Checklist(dealId, vero1Active);
  const history = useHistory();
  return (
    <Band>
      <Info>
        <InternalOnlyChip $tone="dark">Internal only</InternalOnlyChip>
        <TextBlock>
          <TextRow>
            <Primary data-testid="vero1-hold-primary">
              {rereview ? rereviewMessage(rereview) : runLabel(runAt)}
            </Primary>
            {rereview && (
              <OpenReviewLink
                type="button"
                data-testid="open-deal-review"
                onClick={() => openDealReview(history, dealId)}
              >
                Open Deal review →
              </OpenReviewLink>
            )}
            {!rereview && (
              <Hint>Leasing sees no cards until a recommendation exists.</Hint>
            )}
          </TextRow>
          {rereview && (
            <Hint>Leasing sees no cards until a recommendation exists.</Hint>
          )}
        </TextBlock>
      </Info>
      <Action>
        <Vero1ReviewTrigger
          dealId={dealId}
          vero1Active={vero1Active}
          dealLabel={dealLabel}
        />
      </Action>
    </Band>
  );
};
